import { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import hrHR from "dayjs/locale/hr";
import { coownersCol, receiptsCol } from "../../../../firebase.config";
import { addDoc, getDocs } from "firebase/firestore";
import { CoOwner, CustomDate, Location, Receipt } from "../../../interfaces";
import { LocationSelector, CoOwnerSelector } from "./";

interface IProps {
  locations: Location[];
}

export const ReceiptForm = ({ locations }: IProps) => {
  const [coOwners, setCoOwners] = useState<CoOwner[]>([]);
  const [locationId, setLocationId] = useState<string>("");
  const [coOwner, setCoOwner] = useState<string>("");
  const [paid, setPaid] = useState<number>(0);
  const [amount, setAmount] = useState<number>(0);
  const [date, setDate] = useState<CustomDate>({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  useEffect(() => {
    async function fetchCoOwners() {
      const data = await getDocs(coownersCol);

      const results = data.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      const filteredResult = results.filter(
        (r: CoOwner) => r.location === locationId
      );

      setCoOwners(filteredResult);
    }

    fetchCoOwners();
  }, [locationId]);

  const currentDate = new Date();

  const currentMinDate = `${currentDate.getFullYear() - 1}-1-1`;
  const currentMaxDate = `${currentDate.getFullYear() + 2}-1-1`;

  const handleSaveClick = async (e: any) => {
    if (!locationId.length || !coOwner.length || !amount || !paid) return;

    const newReceiptObject: Omit<Receipt, "id"> = {
      location: locationId,
      coowner: coOwner,
      date: { ...date, month: date.month },
      amount,
      paid,
      currency: "EUR"
    };

    try {
      await addDoc(receiptsCol, newReceiptObject);
    } catch (error) {
      console.error("Error during saving receipt to firestore: ", error);
      return;
    }

    setLocationId("");
    setCoOwner("");
    setAmount(0);
    setPaid(0);
    setDate({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
    });
  };

  return (
    <Box
      sx={{
        paddingBlock: "2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1.5rem",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
        Dodaj uplatu
      </Typography>
      <LocationSelector
        locationId={locationId}
        setLocationId={setLocationId}
        locations={locations}
      />
      <CoOwnerSelector
        coOwners={coOwners}
        coOwner={coOwner}
        setCoOwner={setCoOwner}
        disabled={!locationId.length}
      />
      <TextField
        id="outlined-basic"
        label="Za platiti"
        variant="outlined"
        type="number"
        value={amount}
        sx={{ width: "100%", maxWidth: "600px" }}
        onChange={(e) => {
          setAmount(Number(e.target.value));
          setPaid(Number(e.target.value));
          return;
        }}
      />
      <TextField
        id="outlined-basic"
        label="Plaćeno"
        variant="outlined"
        type="number"
        value={paid}
        sx={{ width: "100%", maxWidth: "600px" }}
        onChange={(e) => setPaid(Number(e.target.value))}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={hrHR}>
        <DatePicker
          views={["year", "month"]}
          label="Mjesec i godina"
          minDate={new Date(currentMinDate)}
          maxDate={new Date(currentMaxDate)}
          value={[date.year, date.month]}
          onChange={({ $M, $y }: any) => {
            setDate({
              month: $M + 1,
              year: $y,
            });
          }}
          renderInput={(params: any) => (
            <TextField {...params} helperText={null} />
          )}
        />
      </LocalizationProvider>
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#F1B317",
          color: "#595959",
          "&:hover": {
            backgroundColor: "#F1B317",
            color: "white",
          },
        }}
        onClick={handleSaveClick}
        disabled={!locationId.length || !coOwner.length || !amount || !paid}
      >
        Spremi
      </Button>
    </Box>
  );
};
